// WhatsAppDashboard.js
import React, { useContext, useEffect, useMemo, useState, useCallback, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Grid,
  Avatar,
  Alert,
  LinearProgress,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Divider,
  CircularProgress,
  Tabs,
  Tab,
} from "@mui/material";
import {
  Add as AddIcon,
  Campaign as CampaignIcon,
  ChatBubbleOutline as ChatIcon,
  CheckCircleOutline as CheckIcon,
  Refresh as RefreshIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PeopleAlt as PeopleIcon,
  Block as BlockIcon,
  Event as EventIcon,
  ListAlt as ListIcon,
  Autorenew as SyncIcon,
  VisibilityOutlined as VisibilityIcon,
} from "@mui/icons-material";

import WhatsAppCalendar from "./WhatsAppCalendar";
import CreateCampaignModal from "../components/CreateCampaignModal";
import BlacklistReviewPanel from "./BlacklistReviewPanel";
import BroadcastBudgetPanel from "./BroadcastBudgetPanel";
import WhatsAppCampaignInsightsDialog from "./WhatsAppCampaignInsightsDialog";
import WhatsAppContactInsightsCard from "./WhatsAppContactInsightsCard";
import { AuthContext } from "../AuthContext";
import api from "../axiosConfig";
import { campaignApi } from "../services/campaignApi";

const WA_GREEN = "#28B463";
const BG_GREY = "#F6F7F9";
const BORDER_COLOR = "#E8ECF1";

const soft = (hex, alphaHex = "14") => `${hex}${alphaHex}`;

function CardShell({ title, right, children, sx }) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 4,
        border: "1px solid",
        borderColor: BORDER_COLOR,
        bgcolor: "white",
        overflow: "hidden",
        ...sx,
      }}
    >
      {title ? (
        <>
          <Box sx={{ px: { xs: 2, md: 2.5 }, py: { xs: 1.8, md: 2.1 } }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
              <Typography variant="h6" fontWeight={900}>
                {title}
              </Typography>
              {right ? right : null}
            </Stack>
          </Box>
          <Divider />
        </>
      ) : null}

      <Box sx={{ p: { xs: 2, md: 2.5 } }}>{children}</Box>
    </Paper>
  );
}

function StatTile({ icon, label, value, sub, color = WA_GREEN, sx, valueVariant = "h4" }) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: "1px solid",
        borderColor: BORDER_COLOR,
        p: 2,
        height: "100%",
        bgcolor: "white",
        ...sx,
      }}
    >
      <Stack direction="row" spacing={1.6} alignItems="center">
        <Avatar sx={{ bgcolor: soft(color), color, width: 44, height: 44 }}>
          {icon}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary" fontWeight={700} noWrap>
            {label}
          </Typography>
          <Typography variant={valueVariant} fontWeight={950} sx={{ lineHeight: 1.05 }}>
            {value}
          </Typography>
          {sub ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.35 }}>
              {sub}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </Paper>
  );
}

function DonutChart({
  title,
  items,
  centerLabel,
  centerValue,
  size = 210,
  stroke = 18,
}) {
  // items: [{ label, value, color }]
  const total = items.reduce((s, it) => s + Math.max(0, Number(it.value || 0)), 0);
  const radius = (size - stroke) / 2;
  const c = 2 * Math.PI * radius;

  let acc = 0;
  const arcs = items.map((it) => {
    const v = Math.max(0, Number(it.value || 0));
    const frac = total > 0 ? v / total : 0;
    const dash = frac * c;
    const gap = c - dash;
    const offset = acc;
    acc += dash;
    return { ...it, dash, gap, offset };
  });

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: "1px solid",
        borderColor: BORDER_COLOR,
        p: 2,
        height: "100%",
        bgcolor: "white",
      }}
    >
      <Typography variant="body1" fontWeight={900} sx={{ mb: 1.5 }}>
        {title}
      </Typography>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
        <Box sx={{ position: "relative", width: size, height: size }}>
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
            {/* track */}
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke="#EEF2F7"
              strokeWidth={stroke}
            />
            {/* arcs */}
            <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
              {arcs.map((a, idx) => (
                <circle
                  key={idx}
                  cx={size / 2}
                  cy={size / 2}
                  r={radius}
                  fill="none"
                  stroke={a.color}
                  strokeWidth={stroke}
                  strokeLinecap="round"
                  strokeDasharray={`${a.dash} ${a.gap}`}
                  strokeDashoffset={-a.offset}
                />
              ))}
            </g>
          </svg>

          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              textAlign: "center",
              px: 2,
            }}
          >
            <Typography variant="h3" fontWeight={950} sx={{ lineHeight: 1 }}>
              {centerValue}
            </Typography>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>
              {centerLabel}
            </Typography>
          </Box>
        </Box>

        <Stack spacing={1.2} sx={{ width: "100%" }}>
          {items.map((it) => (
            <Stack key={it.label} direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: 99, bgcolor: it.color }} />
                <Typography variant="body2" fontWeight={800} sx={{ whiteSpace: "nowrap" }}>
                  {it.label}
                </Typography>
              </Stack>
              <Typography variant="body2" fontWeight={900}>
                {Number(it.value || 0).toLocaleString()}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Stack>

      {total === 0 ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
          No audience distribution available yet.
        </Typography>
      ) : null}
    </Paper>
  );
}

function DeliveryFunnel({
  counts,
  hasDeliveredField,
  hasReadField,
}) {
  // counts: { sent, delivered, read, failed }
  const sent = Math.max(0, Number(counts?.sent ?? 0));
  const delivered = Math.max(0, Number(counts?.delivered ?? 0));
  const read = Math.max(0, Number(counts?.read ?? 0));
  const failed = Math.max(0, Number(counts?.failed ?? 0));

  const stages = [
    { key: "sent", label: "Sent", value: sent, color: "#5DADE2" },
    { key: "delivered", label: "Delivered", value: delivered, color: WA_GREEN, requires: "delivered_count" },
    { key: "read", label: "Read", value: read, color: "#6C757D", requires: "read_count" },
    { key: "failed", label: "Failed", value: failed, color: "#E74C3C" },
  ];

  const visible = stages.filter((s) => {
    if (s.key === "delivered" && !hasDeliveredField) return true; // show but annotate
    if (s.key === "read" && !hasReadField) return true; // show but annotate
    return true;
  });

  const totalBase = Math.max(1, sent + failed); // base pipeline size
  const widthPct = (v) => Math.max(3, Math.min(100, (v / totalBase) * 100));

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: "1px solid",
        borderColor: BORDER_COLOR,
        p: 2,
        bgcolor: "white",
        height: "100%",
      }}
    >
      <Typography variant="body1" fontWeight={900} sx={{ mb: 1.5 }}>
        Delivery Funnel
      </Typography>

      <Box
        sx={{
          bgcolor: "#F3F6FA",
          borderRadius: 999,
          overflow: "hidden",
          border: "1px solid",
          borderColor: "#EDF1F6",
          height: 42,
          display: "flex",
        }}
      >
        {visible.map((s) => (
          <Tooltip
            key={s.key}
            title={
              s.requires && (
                (s.requires === "delivered_count" && !hasDeliveredField)
                  ? "Your API is not returning delivered_count yet (showing 0 if missing)."
                  : (s.requires === "read_count" && !hasReadField)
                    ? "Your API is not returning read_count yet (showing 0 if missing)."
                    : ""
              )
            }
            placement="top"
            arrow
          >
            <Box
              sx={{
                width: `${widthPct(s.value)}%`,
                bgcolor: `${s.color}22`,
                borderRight: "1px solid #E9EEF5",
                display: "flex",
                alignItems: "center",
                px: 1.5,
                minWidth: 86,
              }}
            >
              <Typography variant="body2" fontWeight={900} sx={{ color: s.color }}>
                {s.value.toLocaleString()}
              </Typography>
              <Typography
                variant="body2"
                fontWeight={800}
                sx={{ ml: 0.8, color: "text.primary", opacity: 0.9 }}
              >
                {s.label}
              </Typography>
            </Box>
          </Tooltip>
        ))}
      </Box>

      <Stack direction="row" spacing={2} sx={{ mt: 1.5 }} alignItems="center">
        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{ width: 8, height: 8, borderRadius: 99, bgcolor: "#5DADE2" }} />
          <Typography variant="caption" color="text.secondary" fontWeight={800}>
            Sent
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{ width: 8, height: 8, borderRadius: 99, bgcolor: WA_GREEN }} />
          <Typography variant="caption" color="text.secondary" fontWeight={800}>
            Delivered
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{ width: 8, height: 8, borderRadius: 99, bgcolor: "#6C757D" }} />
          <Typography variant="caption" color="text.secondary" fontWeight={800}>
            Read
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{ width: 8, height: 8, borderRadius: 99, bgcolor: "#E74C3C" }} />
          <Typography variant="caption" color="text.secondary" fontWeight={800}>
            Failed
          </Typography>
        </Stack>
      </Stack>
    </Paper>
  );
}

const statusChipColor = (status, failedCount = 0) => {
  const st = String(status || "").toLowerCase();
  const hasFailed = Number(failedCount || 0) > 0;

  if (st === "failed") return "error";
  if (st === "completed" && hasFailed) return "error";
  if (["active", "sending"].includes(st)) return hasFailed ? "error" : "success";
  if (st === "scheduled") return "warning";
  if (st === "paused") return "default";
  return "default";
};

const formatStatus = (s) => (s ? String(s).replace(/_/g, " ") : "unknown");

const ensureSeconds = (hhmmOrHhmmss) => {
  if (!hhmmOrHhmmss) return null;
  const v = String(hhmmOrHhmmss).trim();
  if (/^\d{2}:\d{2}$/.test(v)) return `${v}:00`;
  if (/^\d{2}:\d{2}:\d{2}$/.test(v)) return v;
  return v;
};

const toIsoFromDatetimeLocal = (value) => {
  if (!value) return null;
  return String(value).trim();
};

const pad2 = (n) => String(n).padStart(2, "0");

const getMinDatetimeLocalNow = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(
    d.getHours()
  )}:${pad2(d.getMinutes())}`;
};

const toBudgetInputValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  return String(value);
};

const parseBudgetInputValue = (value, fallback = null) => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) {
    return fallback;
  }

  const numeric = Number(trimmed);
  return Number.isFinite(numeric) ? numeric : Number.NaN;
};

const toDatetimeLocalFromIso = (iso, tz) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";

  const safeTz = tz || "Africa/Harare";

  try {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: safeTz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).formatToParts(d);

    const get = (type) => parts.find((p) => p.type === type)?.value || "";
    const yyyy = get("year");
    const mm = get("month");
    const dd = get("day");
    const hh = get("hour");
    const mi = get("minute");
    if (yyyy && mm && dd && hh && mi) return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
  } catch {
    // ignore
  }

  const yyyy = d.getFullYear();
  const mm = pad2(d.getMonth() + 1);
  const dd = pad2(d.getDate());
  const hh = pad2(d.getHours());
  const mi = pad2(d.getMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
};

const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const isSameHour = (a, b) =>
  isSameDay(a, b) && a.getHours() === b.getHours();
const MAX_CAMPAIGNS_PER_HOUR = 3;

function ConfirmDeleteDialog({ open, onClose, onConfirm, campaignName, busy }) {
  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Delete campaign?</DialogTitle>
      <DialogContent dividers>
        <Typography>
          Are you sure you want to delete <strong>{campaignName || "this campaign"}</strong>? This cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button color="error" variant="contained" onClick={onConfirm} disabled={busy}>
          {busy ? "Deleting..." : "Delete"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function CampaignDialog({ open, mode, initialCampaign, onClose, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [checking, setChecking] = useState(false);
  const [conflictMsg, setConflictMsg] = useState("");

  const isEdit = mode === "edit";

  const [form, setForm] = useState({
    name: "",
    whatsapp_template_id: "",
    target_audience: "all",
    schedule_at: "",
    messages_per_hour: 120,
    cooldown_hours: 0,
    send_window_start: "08:00",
    send_window_end: "17:00",
    timezone: "Africa/Harare",
    max_attempts: 3,
  });

  const isLockedByStatus = useMemo(() => {
    const st = String(initialCampaign?.status || "").toLowerCase();
    return ["active", "sending", "completed", "failed"].includes(st);
  }, [initialCampaign?.status]);

  useEffect(() => {
    if (!open) return;
    setError("");
    setConflictMsg("");

    if (isEdit && initialCampaign) {
      const tz = initialCampaign?.timezone ?? "Africa/Harare";
      setForm({
        name: initialCampaign?.name ?? "",
        whatsapp_template_id: initialCampaign?.whatsapp_template_id ?? "",
        target_audience: initialCampaign?.target_audience ?? "all",
        schedule_at: initialCampaign?.schedule_at ? toDatetimeLocalFromIso(initialCampaign.schedule_at, tz) : "",
        messages_per_hour: Number(initialCampaign?.messages_per_hour ?? 120),
        cooldown_hours: Number(initialCampaign?.cooldown_hours ?? 0),
        send_window_start: String(initialCampaign?.send_window_start ?? "08:00").slice(0, 5),
        send_window_end: String(initialCampaign?.send_window_end ?? "17:00").slice(0, 5),
        timezone: tz,
        max_attempts: Number(initialCampaign?.max_attempts ?? 3),
      });
    } else {
      setForm({
        name: "",
        whatsapp_template_id: "",
        target_audience: "all",
        schedule_at: "",
        messages_per_hour: 120,
        cooldown_hours: 0,
        send_window_start: "08:00",
        send_window_end: "17:00",
        timezone: "Africa/Harare",
        max_attempts: 3,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const setField = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));

  const normalizeCalendarPayload = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.data?.data)) return payload.data.data;
    return [];
  };

  const validateNoOverlap = useCallback(async () => {
    setConflictMsg("");
    if (!form.schedule_at) return true;

    const picked = new Date(form.schedule_at);
    if (Number.isNaN(picked.getTime())) return true;

    const now = new Date();
    if (picked <= now) {
      setConflictMsg("Schedule time must be in the future.");
      return false;
    }

    setChecking(true);
    try {
      const day = new Date(picked);
      const dayOfWeek = (day.getDay() + 6) % 7; // Monday=0
      const monday = new Date(day);
      monday.setDate(day.getDate() - dayOfWeek);

      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);

      const toYmd = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

      const res = await campaignApi.getCalendar(toYmd(monday), toYmd(sunday));
      const safe = normalizeCalendarPayload(res?.data ?? res);

      const myId = isEdit ? String(initialCampaign?.id ?? "") : "";

      const sameHourArr = safe.filter((c) => {
        if (!c?.date) return false;
        const dt = new Date(c.date);
        if (Number.isNaN(dt.getTime())) return false;
        if (myId && String(c?.id ?? "") === myId) return false;
        return isSameHour(dt, picked);
      });

      if (sameHourArr.length >= MAX_CAMPAIGNS_PER_HOUR) {
        setConflictMsg(`This hour already has ${MAX_CAMPAIGNS_PER_HOUR} campaigns scheduled. Choose another time.`);
        return false;
      }

      return true;
    } catch (e) {
      console.warn("Calendar validation skipped (failed to load calendar)", e);
      return true;
    } finally {
      setChecking(false);
    }
  }, [form.schedule_at, isEdit, initialCampaign]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => validateNoOverlap(), 250);
    return () => clearTimeout(t);
  }, [open, form.schedule_at, validateNoOverlap]);

  const submit = async () => {
    if (isEdit && isLockedByStatus) {
      setError("This campaign is locked because it is currently running or finished.");
      return;
    }

    setBusy(true);
    setError("");

    try {
      const ok = await validateNoOverlap();
      if (!ok) {
        setBusy(false);
        return;
      }

      const payload = {
        name: String(form.name || "").trim(),
        whatsapp_template_id: String(form.whatsapp_template_id || "").trim(),
        target_audience: form.target_audience,
        messages_per_hour: Number(form.messages_per_hour),
        cooldown_hours: Number(form.cooldown_hours),
        send_window_start: ensureSeconds(form.send_window_start),
        send_window_end: ensureSeconds(form.send_window_end),
        timezone: String(form.timezone || "Africa/Harare").trim(),
        max_attempts: Number(form.max_attempts),
        schedule_at: form.schedule_at ? toIsoFromDatetimeLocal(form.schedule_at) : null,
      };

      if (!payload.name) throw new Error("Campaign name is required.");
      if (!payload.whatsapp_template_id) throw new Error("Template ID is required.");

      await campaignApi.update(initialCampaign.id, payload);

      onSaved?.();
      onClose();
    } catch (e) {
      setError(
        e?.response?.data?.message ||
          (typeof e?.response?.data === "string" ? e.response.data : "") ||
          e?.message ||
          "Failed to update campaign"
      );
    } finally {
      setBusy(false);
    }
  };

  const disableSave = busy || checking || Boolean(conflictMsg) || (isEdit && isLockedByStatus);
  const fieldDisabled = busy || (isEdit && isLockedByStatus);

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>Edit WhatsApp Campaign</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {isEdit && isLockedByStatus && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Locked: this campaign is running or finished, so editing is disabled.
          </Alert>
        )}
        {conflictMsg && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {conflictMsg}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField label="Campaign Name" value={form.name} onChange={setField("name")} fullWidth required disabled={fieldDisabled} />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="WhatsApp Template ID"
              value={form.whatsapp_template_id}
              onChange={setField("whatsapp_template_id")}
              fullWidth
              required
              disabled={fieldDisabled}
              helperText="Must match your WhatsApp provider template ID"
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField select label="Target Audience" value={form.target_audience} onChange={setField("target_audience")} fullWidth disabled={fieldDisabled}>
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="standard">Standard</MenuItem>
              <MenuItem value="vip">VIP</MenuItem>
              <MenuItem value="exclude_vip">Exclude VIP</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField label="Timezone" value={form.timezone} onChange={setField("timezone")} fullWidth disabled={fieldDisabled} />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label="Schedule At (optional)"
              type="datetime-local"
              value={form.schedule_at}
              onChange={setField("schedule_at")}
              fullWidth
              disabled={fieldDisabled}
              InputLabelProps={{ shrink: true }}
              helperText="Leave empty to launch immediately"
              inputProps={{ min: getMinDatetimeLocalNow() }}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField label="Messages per hour" type="number" value={form.messages_per_hour} onChange={setField("messages_per_hour")} fullWidth disabled={fieldDisabled} inputProps={{ min: 1, max: 5000 }} />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField label="Cooldown hours" type="number" value={form.cooldown_hours} onChange={setField("cooldown_hours")} fullWidth disabled={fieldDisabled} inputProps={{ min: 0, max: 720 }} />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField label="Send window start" type="time" value={form.send_window_start} onChange={setField("send_window_start")} fullWidth disabled={fieldDisabled} InputLabelProps={{ shrink: true }} />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField label="Send window end" type="time" value={form.send_window_end} onChange={setField("send_window_end")} fullWidth disabled={fieldDisabled} InputLabelProps={{ shrink: true }} />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField label="Max attempts" type="number" value={form.max_attempts} onChange={setField("max_attempts")} fullWidth disabled={fieldDisabled} inputProps={{ min: 1, max: 10 }} />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={disableSave}
          sx={{ bgcolor: WA_GREEN, "&:hover": { bgcolor: "#219150" }, boxShadow: "none" }}
        >
          {isEdit && isLockedByStatus ? "Locked (running/finished)" : checking ? "Checking..." : busy ? "Saving..." : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function WhatsAppDashboard({ intelligenceAudienceId = null, onIntelligenceAudienceConsumed }) {
  const { user } = useContext(AuthContext);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  const [summary, setSummary] = useState({
    total_contacts: 0,
    contacts_contacted: 0,
    contacts_not_contacted: 0,
    contacts_blocked: 0,
  });
  const [summaryLoading, setSummaryLoading] = useState(false);

  const [tab, setTab] = useState(0);
  const [blacklistOpen, setBlacklistOpen] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("create");
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [insightsOpen, setInsightsOpen] = useState(false);
  const [insightsCampaign, setInsightsCampaign] = useState(null);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const [calendarKey, setCalendarKey] = useState(0);
  const [budgetChannel, setBudgetChannel] = useState(null);
  const [budgetLoading, setBudgetLoading] = useState(false);
  const [budgetDialogOpen, setBudgetDialogOpen] = useState(false);
  const [budgetSaving, setBudgetSaving] = useState(false);
  const [budgetError, setBudgetError] = useState("");
  const [budgetForm, setBudgetForm] = useState({
    daily_limit: "",
    monthly_limit: "",
    reserve_buffer: "",
  });

  const pollTimerRef = useRef(null);
  const lastSigRef = useRef("");

  const [pendingIntelligenceId, setPendingIntelligenceId] = useState(null);

  useEffect(() => {
    if (!intelligenceAudienceId) return;
    setPendingIntelligenceId(intelligenceAudienceId);
    setDialogMode("create");
    setDialogOpen(true);
    onIntelligenceAudienceConsumed?.();
  }, [intelligenceAudienceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const normalizeRows = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.data?.data)) return payload.data.data;
    return [];
  };

  const computeSignature = (rows) => {
    try {
      return (rows || [])
        .map(
          (c) =>
            `${c?.id}:${c?.status}:${c?.updated_at ?? ""}:${c?.total_count ?? 0}:${c?.sent_count ?? 0}:${c?.failed_count ?? 0}:${c?.queued_count ?? 0}:${c?.delivered_count ?? ""}:${c?.read_count ?? ""}`
        )
        .join("|");
    } catch {
      return String(Date.now());
    }
  };

  const canManageBudget = useMemo(() => {
    const role = String(user?.role || "").toLowerCase().trim();
    return Boolean(user?.is_admin || ["marketing", "admin", "superadmin"].includes(role));
  }, [user]);

  const hydrateBudgetForm = useCallback((channel) => {
    setBudgetForm({
      daily_limit: toBudgetInputValue(channel?.settings?.daily_limit ?? channel?.daily_limit),
      monthly_limit: toBudgetInputValue(channel?.settings?.monthly_limit ?? channel?.monthly_limit),
      reserve_buffer: toBudgetInputValue(channel?.settings?.reserve_buffer ?? channel?.reserve_buffer ?? 0),
    });
  }, []);

  const normalizeBudgetChannel = useCallback((payload) => {
    const channels = payload?.data?.channels || payload?.channels || {};
    return channels?.whatsapp || null;
  }, []);

  const loadCampaigns = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    if (!silent) setErrorMsg("");

    try {
      const res = await campaignApi.list({ page: 1, channel: "whatsapp", per_page: 200 });
      const rows = normalizeRows(res?.data);

      const sig = computeSignature(rows);
      const changed = sig !== lastSigRef.current;
      lastSigRef.current = sig;

      setCampaigns(rows);
      if (changed) setCalendarKey((k) => k + 1);
    } catch (err) {
      if (!silent) {
        setCampaigns([]);
        setErrorMsg(err?.response?.data?.message || err?.message || "Failed to load WhatsApp campaigns");
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  const loadSummary = useCallback(async (silent = false) => {
    if (!silent) setSummaryLoading(true);
    try {
      const res = await campaignApi.summary();
      const data = res?.data ?? res;

      setSummary({
        total_contacts: Number(data?.total_contacts ?? 0),
        contacts_contacted: Number(data?.contacts_contacted ?? 0),
        contacts_not_contacted: Number(data?.contacts_not_contacted ?? Math.max(0, Number(data?.total_contacts ?? 0) - Number(data?.contacts_contacted ?? 0))),
        contacts_blocked: Number(data?.contacts_blocked ?? 0),
      });
    } catch (err) {
      console.warn("Failed to load campaigns summary", err);
    } finally {
      if (!silent) setSummaryLoading(false);
    }
  }, []);

  const loadBudget = useCallback(
    async (silent = false) => {
      if (!user) {
        return;
      }

      if (!silent) {
        setBudgetLoading(true);
      }

      try {
        const res = await api.get("/analytics/marketing/broadcast-budget");
        const channel = normalizeBudgetChannel(res?.data);

        setBudgetChannel(channel);
        if (!budgetDialogOpen) {
          setBudgetError("");
        }
      } catch (err) {
        if (!silent) {
          setBudgetError(err?.response?.data?.message || err?.message || "Failed to load WhatsApp budget");
        }
      } finally {
        if (!silent) {
          setBudgetLoading(false);
        }
      }
    },
    [budgetDialogOpen, normalizeBudgetChannel, user]
  );

  useEffect(() => {
    loadCampaigns(false);
    loadSummary(false);
  }, [loadCampaigns, loadSummary]);

  useEffect(() => {
    loadBudget(false);
  }, [loadBudget]);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const startPolling = useCallback(
    (intervalMs) => {
      stopPolling();
      pollTimerRef.current = setInterval(() => {
        if (!document.hidden) {
          loadCampaigns(true);
          loadSummary(true);
          loadBudget(true);
        }
      }, intervalMs);
    },
    [loadBudget, loadCampaigns, loadSummary, stopPolling]
  );

  useEffect(() => {
    const hasLive = (campaigns || []).some((c) => {
      const st = String(c?.status || "").toLowerCase();
      const queued = Number(c?.queued_count ?? 0);
      return ["scheduled", "active"].includes(st) || queued > 0;
    });

    startPolling(hasLive ? 10000 : 60000);
    return () => stopPolling();
  }, [campaigns, startPolling, stopPolling]);

  useEffect(() => {
    const onVis = () => {
      if (!document.hidden) {
        loadCampaigns(true);
        loadSummary(true);
        loadBudget(true);
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [loadBudget, loadCampaigns, loadSummary]);

  const refreshAll = async () => {
    await Promise.all([loadCampaigns(false), loadSummary(false), loadBudget(false)]);
    setCalendarKey((k) => k + 1);
  };

  const openCreate = () => {
    setSelectedCampaign(null);
    setDialogMode("create");
    setDialogOpen(true);
  };

  const openEdit = (campaign) => {
    if (!campaign) return;
    setSelectedCampaign(campaign);
    setDialogMode("edit");
    setDialogOpen(true);
  };

  const openInsights = (campaign) => {
    if (!campaign) return;
    setInsightsCampaign(campaign);
    setInsightsOpen(true);
  };

  const handleCalendarSelect = async (calendarItem) => {
    const id = calendarItem?.id;
    if (!id) return;

    const found = campaigns.find((c) => String(c?.id) === String(id));
    if (found) {
      openEdit(found);
      return;
    }

    await loadCampaigns(true);
    const found2 = campaigns.find((c) => String(c?.id) === String(id));
    if (found2) openEdit(found2);
  };

  const confirmDelete = (campaign) => {
    setSelectedCampaign(campaign);
    setDeleteOpen(true);
  };

  const doDelete = async () => {
    if (!selectedCampaign?.id) return;
    setDeleteBusy(true);
    setErrorMsg("");

    try {
      await campaignApi.remove(selectedCampaign.id);
      setDeleteOpen(false);
      setSelectedCampaign(null);
      await refreshAll();
    } catch (e) {
      setErrorMsg(e?.response?.data?.message || e?.message || "Failed to delete campaign");
    } finally {
      setDeleteBusy(false);
    }
  };

  const pauseCampaign = async (id) => {
    try {
      await campaignApi.pause(id);
      await refreshAll();
    } catch (e) {
      setErrorMsg(e?.response?.data?.message || "Failed to pause campaign");
    }
  };

  const resumeCampaign = async (id) => {
    try {
      await campaignApi.resume(id);
      await refreshAll();
    } catch (e) {
      setErrorMsg(e?.response?.data?.message || "Failed to resume campaign");
    }
  };

  const openBudgetSettings = () => {
    hydrateBudgetForm(budgetChannel);
    setBudgetError("");
    setBudgetDialogOpen(true);
  };

  const closeBudgetSettings = () => {
    if (budgetSaving) {
      return;
    }

    setBudgetDialogOpen(false);
    setBudgetError("");
  };

  const setBudgetField = (field) => (event) => {
    setBudgetForm((previous) => ({
      ...previous,
      [field]: event.target.value,
    }));
  };

  const saveBudgetSettings = async () => {
    const dailyLimit = parseBudgetInputValue(budgetForm.daily_limit);
    const monthlyLimit = parseBudgetInputValue(budgetForm.monthly_limit);
    const reserveBuffer = parseBudgetInputValue(budgetForm.reserve_buffer, 0);

    if (Number.isNaN(dailyLimit) || Number.isNaN(monthlyLimit) || Number.isNaN(reserveBuffer)) {
      setBudgetError("Budget values must be valid numbers.");
      return;
    }

    setBudgetSaving(true);
    setBudgetError("");

    try {
      const response = await api.put("/analytics/marketing/broadcast-budget/whatsapp", {
        daily_limit: dailyLimit,
        monthly_limit: monthlyLimit,
        reserve_buffer: reserveBuffer ?? 0,
      });

      const channel = normalizeBudgetChannel(response?.data);
      setBudgetChannel(channel);
      hydrateBudgetForm(channel);
      setBudgetDialogOpen(false);
    } catch (err) {
      setBudgetError(err?.response?.data?.message || err?.message || "Failed to save WhatsApp budget");
    } finally {
      setBudgetSaving(false);
    }
  };

  const hasCampaigns = campaigns.length > 0;

  const contactKpis = useMemo(() => {
    const total = Number(summary?.total_contacts ?? 0);
    const contacted = Number(summary?.contacts_contacted ?? 0);
    const notContacted = Number(summary?.contacts_not_contacted ?? Math.max(0, total - contacted));
    const blocked = Number(summary?.contacts_blocked ?? 0);
    const availableReach = Math.max(0, total - contacted - blocked);
    return { total, contacted, notContacted, blocked, availableReach };
  }, [summary]);

  const aggregates = useMemo(() => {
    const totalMsgs = campaigns.reduce((sum, c) => sum + Number(c?.total_count ?? 0), 0);
    const sent = campaigns.reduce((sum, c) => sum + Number(c?.sent_count ?? 0), 0);
    const failed = campaigns.reduce((sum, c) => sum + Number(c?.failed_count ?? 0), 0);
    const queued = campaigns.reduce((sum, c) => sum + Number(c?.queued_count ?? 0), 0);

    const hasDeliveredField = campaigns.some((c) => Object.prototype.hasOwnProperty.call(c || {}, "delivered_count"));
    const hasReadField = campaigns.some((c) => Object.prototype.hasOwnProperty.call(c || {}, "read_count"));

    const delivered = campaigns.reduce((sum, c) => sum + Number(c?.delivered_count ?? 0), 0);
    const read = campaigns.reduce((sum, c) => sum + Number(c?.read_count ?? 0), 0);
    const unread = Math.max(0, delivered - read);

    const deliveryBase = delivered + failed;
    const deliveryRate = deliveryBase > 0 ? Math.round((delivered / deliveryBase) * 100) : 0;

    const activeCampaigns = campaigns.filter((c) => ["active", "sending"].includes(String(c?.status || "").toLowerCase())).length;
    const scheduledCampaigns = campaigns.filter((c) => String(c?.status || "").toLowerCase() === "scheduled").length;

    const latest = campaigns
      .map((c) => c?.updated_at || c?.last_run_at || c?.last_sent_at || c?.created_at)
      .filter(Boolean)
      .map((x) => new Date(x))
      .filter((d) => !Number.isNaN(d.getTime()))
      .sort((a, b) => b.getTime() - a.getTime())[0];

    // msgs/hr: show current sending throughput (sum for active/scheduled), else overall average if exists
    const speedBase = campaigns.filter((c) => ["active", "scheduled", "sending"].includes(String(c?.status || "").toLowerCase()));
    const speed = (speedBase.length ? speedBase : campaigns).reduce((sum, c) => sum + Number(c?.messages_per_hour ?? 0), 0);

    return {
      totalMsgs,
      sent,
      failed,
      queued,
      delivered,
      read,
      unread,
      deliveryRate,
      activeCampaigns,
      scheduledCampaigns,
      latestRunDate: latest || null,
      msgsPerHour: speed,
      hasDeliveredField,
      hasReadField,
    };
  }, [campaigns]);

  const formatLastRun = (d) => {
    if (!d) return "—";
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    if (diffMs < 0) return "Just now";
    const mins = Math.floor(diffMs / (1000 * 60));
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs} hours ago`;
    const days = Math.floor(hrs / 24);
    return `${days} days ago`;
  };

  const formatInTimeZone = (isoString, tz) => {
    if (!isoString) return "Immediate";
    const d = new Date(isoString);
    if (Number.isNaN(d.getTime())) return "Immediate";
    try {
      return new Intl.DateTimeFormat(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
        timeZone: tz || "Africa/Harare",
      }).format(d);
    } catch {
      return d.toLocaleString();
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: BG_GREY, width: "100%" }}>
      {/* Full-width page wrapper */}
      <Box sx={{ px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 }, width: "100%" }}>
        {/* Header (matches screenshot layout: title left, refresh + button right) */}
        <Box sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} flexWrap="wrap">
            <Box>
              <Typography variant="h4" fontWeight={950} sx={{ letterSpacing: -0.2, fontSize: { xs: "1.5rem", sm: "2.125rem" } }}>
                WhatsApp Broadcasts
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.2 }}>
                Manage and schedule bulk WhatsApp campaigns
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} alignItems="center">
              <Tooltip title="Reload">
                <span>
                  <IconButton onClick={refreshAll} disabled={loading || summaryLoading || budgetLoading} sx={{ bgcolor: "white", border: `1px solid ${BORDER_COLOR}` }}>
                    <RefreshIcon />
                  </IconButton>
                </span>
              </Tooltip>

              <Button
                variant="contained"
                startIcon={<AddIcon />}
                sx={{
                  bgcolor: WA_GREEN,
                  "&:hover": { bgcolor: "#219150" },
                  borderRadius: 3,
                  textTransform: "none",
                  px: 2.6,
                  fontWeight: 900,
                  boxShadow: "none",
                  height: 44,
                }}
                onClick={openCreate}
              >
                New Campaign
              </Button>
            </Stack>
          </Stack>

          {(loading || summaryLoading) ? (
            <LinearProgress sx={{ mt: 2, borderRadius: 99 }} />
          ) : null}
        </Box>

        {errorMsg ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMsg}
          </Alert>
        ) : null}

        {budgetError && !budgetDialogOpen ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {budgetError}
          </Alert>
        ) : null}

        <Box sx={{ mb: 2 }}>
          <BroadcastBudgetPanel
            title="WhatsApp Budget Guard"
            subtitle="Shared across all WhatsApp campaigns and users. If Meta balance becomes available, the guard uses the lower of your manual cap and Meta remaining."
            channels={budgetChannel ? [budgetChannel] : []}
            loading={budgetLoading}
            actions={
              canManageBudget ? (
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={openBudgetSettings}
                  disabled={budgetLoading}
                  sx={{ borderRadius: 999, textTransform: "none", fontWeight: 800 }}
                >
                  Budget settings
                </Button>
              ) : null
            }
          />
        </Box>

        {/* ======= NEW: Top dashboard area (full-width tiles, matches screenshot structure) ======= */}
        <CardShell title="Campaign Performance" sx={{ mb: 2 }}>
          <Grid container spacing={2} sx={{ width: "100%", m: 0 }}>
            {/* Left column: Success rate + Delivery funnel */}
            <Grid item xs={12} md={4}>
              <Stack spacing={2} sx={{ height: "100%" }}>
                <StatTile
                  icon={<CheckIcon />}
                  label="Success Rate"
                  value={`${aggregates.deliveryRate}%`}
                  color={WA_GREEN}
                  sub={
                    aggregates.totalMsgs > 0
                      ? `${aggregates.delivered.toLocaleString()} delivered / ${Math.max(0, aggregates.delivered + aggregates.failed).toLocaleString()} processed`
                      : "No campaign targets yet"
                  }
                  valueVariant="h3"
                />

                <DeliveryFunnel
                  counts={{
                    sent: aggregates.sent,
                    delivered: aggregates.delivered,
                    read: aggregates.read,
                    failed: aggregates.failed,
                  }}
                  hasDeliveredField={aggregates.hasDeliveredField}
                  hasReadField={aggregates.hasReadField}
                />
              </Stack>
            </Grid>

            {/* Middle column: Active campaigns + Total contacts */}
            <Grid item xs={12} md={4}>
              <Stack spacing={2} sx={{ height: "100%" }}>
                <StatTile
                  icon={<CampaignIcon />}
                  label="Active Campaigns"
                  value={aggregates.activeCampaigns.toLocaleString()}
                  color={WA_GREEN}
                  sub={`${Math.max(0, aggregates.scheduledCampaigns)} scheduled`}
                  valueVariant="h3"
                />

                <StatTile
                  icon={<PeopleIcon />}
                  label="Total Contacts"
                  value={contactKpis.total.toLocaleString()}
                  color={WA_GREEN}
                  sub={
                    contactKpis.total > 0
                      ? `+${Math.max(0, contactKpis.contacted)} contacted`
                      : "No contacts imported yet"
                  }
                  valueVariant="h3"
                />
              </Stack>
            </Grid>

            {/* Right column: Audience distribution donut */}
            <Grid item xs={12} md={4}>
              <DonutChart
                title="Audience Distribution"
                centerValue={contactKpis.contacted.toLocaleString()}
                centerLabel="Contacted"
                items={[
                  { label: "Contacted", value: contactKpis.contacted, color: WA_GREEN },
                  { label: "Available Reach", value: contactKpis.availableReach, color: "#DDE5EF" },
                  { label: "Blocked", value: contactKpis.blocked, color: "#E74C3C" },
                ]}
              />
            </Grid>
          </Grid>
        </CardShell>

        <CardShell
          title="Activity Overview"
          sx={{ mb: 2 }}
        >
          <Grid container spacing={2} sx={{ width: "100%", m: 0 }}>
            <Grid item xs={12} md={3}>
              <StatTile
                icon={<SyncIcon />}
                label="Last Campaign Run"
                value={formatLastRun(aggregates.latestRunDate)}
                color={WA_GREEN}
                sub={aggregates.latestRunDate ? aggregates.latestRunDate.toLocaleString() : "No runs recorded yet"}
                valueVariant="h5"
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <StatTile
                icon={<EventIcon />}
                label="Scheduled Campaigns"
                value={aggregates.scheduledCampaigns.toLocaleString()}
                color={WA_GREEN}
                sub={aggregates.scheduledCampaigns ? "Queued for execution" : "No scheduled broadcasts"}
                valueVariant="h5"
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <StatTile
                icon={<ChatIcon />}
                label="msgs/hr"
                value={aggregates.msgsPerHour.toLocaleString()}
                color={WA_GREEN}
                sub={aggregates.activeCampaigns ? "Throughput for active/scheduled" : "Configured send speed"}
                valueVariant="h5"
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <Stack spacing={1}>
                <StatTile
                  icon={<BlockIcon />}
                  label="Blacklisted"
                  value={contactKpis.blocked.toLocaleString()}
                  color="#E74C3C"
                  sub="Auto-blacklisted contacts"
                  valueVariant="h5"
                />
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  startIcon={<BlockIcon />}
                  sx={{ textTransform: "none", fontWeight: 800 }}
                  onClick={() => setBlacklistOpen(true)}
                >
                  Review Blacklist
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </CardShell>

        <BlacklistReviewPanel
          open={blacklistOpen}
          onClose={() => setBlacklistOpen(false)}
          onChanged={() => loadSummary(true)}
        />

        {/* ======= Existing main content (Campaigns + Calendar) ======= */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: "1px solid",
            borderColor: BORDER_COLOR,
            bgcolor: "white",
            overflow: "hidden",
          }}
        >
          <Box sx={{ px: { xs: 2, md: 2.5 }, pt: 1 }}>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              variant="scrollable"
              allowScrollButtonsMobile
              sx={{ "& .MuiTab-root": { textTransform: "none", fontWeight: 900 } }}
            >
              <Tab icon={<ListIcon />} iconPosition="start" label="Campaigns" />
              <Tab icon={<EventIcon />} iconPosition="start" label="Calendar" />
            </Tabs>
          </Box>

          <Divider />

          {/* Tab: Campaigns */}
          {tab === 0 && (
            <>
              {!hasCampaigns ? (
                <Box sx={{ py: 8, textAlign: "center", px: 2 }}>
                  <Avatar
                    sx={{
                      width: 88,
                      height: 88,
                      bgcolor: "#f0fff4",
                      border: `1px dashed ${WA_GREEN}`,
                      mx: "auto",
                      mb: 2,
                    }}
                  >
                    <CampaignIcon sx={{ fontSize: 44, color: WA_GREEN }} />
                  </Avatar>

                  <Typography variant="h6" fontWeight={950} gutterBottom>
                    No campaigns yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 520, mx: "auto", mb: 3 }}>
                    Create your first WhatsApp broadcast to reach customers instantly.
                  </Typography>

                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    sx={{
                      bgcolor: WA_GREEN,
                      "&:hover": { bgcolor: "#219150" },
                      borderRadius: 3,
                      textTransform: "none",
                      px: 3,
                      py: 1.1,
                      fontWeight: 950,
                      boxShadow: "none",
                    }}
                    onClick={openCreate}
                  >
                    Create Campaign
                  </Button>
                </Box>
              ) : (
                <TableContainer sx={{ maxHeight: 620 }}>
                  <Table stickyHeader>
                    <TableHead sx={{ bgcolor: BG_GREY }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 950 }}>Campaign</TableCell>
                        <TableCell sx={{ fontWeight: 950 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 950 }}>Progress</TableCell>
                        <TableCell sx={{ fontWeight: 950 }}>Speed</TableCell>
                        <TableCell sx={{ fontWeight: 950 }}>Schedule</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 950 }}>
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {campaigns.map((c) => {
                        const total = Number(c?.total_count ?? 0);
                        const sent = Number(c?.sent_count ?? 0);
                        const delivered = Number(c?.delivered_count ?? 0);
                        const read = Number(c?.read_count ?? 0);
                        const unread = Math.max(0, delivered - read);
                        const failed = Number(c?.failed_count ?? 0);
                        const queued = Number(c?.queued_count ?? 0);
                        const done = sent + failed;
                        const progress = total > 0 ? Math.round((done / total) * 100) : 0;

                        const st = String(c?.status || "unknown").toLowerCase();
                        const showSendingAnim =
                          (["scheduled", "active"].includes(st) && queued > 0) ||
                          (st === "active" && done < total && total > 0);

                        const scheduleLabel = c?.schedule_at ? formatInTimeZone(c.schedule_at, c.timezone) : "Immediate";

                        const canPause = st === "active";
                        const canResume = st === "paused";
                        const isFinished = ["completed", "failed"].includes(st);

                        return (
                          <TableRow
                            key={c.id}
                            hover
                            sx={{
                              "& td": { borderColor: "#F0F2F6" },
                              ...(showSendingAnim
                                ? {
                                    position: "relative",
                                    animation: "waPulse 1.6s ease-in-out infinite",
                                    "@keyframes waPulse": {
                                      "0%": { backgroundColor: "transparent" },
                                      "50%": { backgroundColor: "rgba(40, 180, 99, 0.06)" },
                                      "100%": { backgroundColor: "transparent" },
                                    },
                                  }
                                : {}),
                            }}
                          >
                            <TableCell>
                              <Typography fontWeight={950}>{c?.name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                Target: {c?.target_audience ?? "—"}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Chip
                                  size="small"
                                  label={formatStatus(c?.status)}
                                  color={statusChipColor(c?.status, failed)}
                                  sx={{ textTransform: "capitalize", fontWeight: 950 }}
                                />
                                {showSendingAnim && (
                                  <Tooltip title="Sending in progress">
                                    <CircularProgress size={14} />
                                  </Tooltip>
                                )}
                              </Stack>
                            </TableCell>

                            <TableCell sx={{ minWidth: 240 }}>
                              <Stack spacing={0.7}>
                                <Stack direction="row" justifyContent="space-between">
                                  <Typography variant="body2" fontWeight={950}>
                                    {progress}%
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {done}/{total}
                                  </Typography>
                                </Stack>

                                <LinearProgress
                                  variant={showSendingAnim ? "indeterminate" : "determinate"}
                                  value={showSendingAnim ? undefined : progress}
                                  sx={{
                                    height: 8,
                                    borderRadius: 999,
                                    bgcolor: "#EEF2F7",
                                    "& .MuiLinearProgress-bar": {
                                      borderRadius: 999,
                                      bgcolor: WA_GREEN,
                                    },
                                    ...(showSendingAnim
                                      ? {
                                          "& .MuiLinearProgress-bar1Indeterminate": { bgcolor: WA_GREEN },
                                          "& .MuiLinearProgress-bar2Indeterminate": { bgcolor: WA_GREEN },
                                        }
                                      : {}),
                                  }}
                                />

                                <Typography variant="caption" color="text.secondary">
                                  Sent {sent} • Delivered {delivered} • Read {read}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Unread {unread} • Failed {failed} • Pending {queued}
                                </Typography>
                              </Stack>
                            </TableCell>

                            <TableCell>
                              <Typography variant="body2" fontWeight={950}>
                                {c?.messages_per_hour ?? "—"}/hr
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Cooldown {c?.cooldown_hours ?? "—"}h
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Typography variant="body2" fontWeight={950}>
                                {scheduleLabel}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                TZ: {c?.timezone ?? "—"}
                              </Typography>
                            </TableCell>

                            <TableCell align="right">
                              <Stack direction="row" spacing={1} justifyContent="flex-end">
                                <Tooltip title="View delivery and recipient breakdown">
                                  <IconButton onClick={() => openInsights(c)}>
                                    <VisibilityIcon />
                                  </IconButton>
                                </Tooltip>

                                <Tooltip title={isFinished ? "Finished campaigns cannot be edited" : "Edit campaign"}>
                                  <span>
                                    <IconButton onClick={() => openEdit(c)} disabled={isFinished}>
                                      <EditIcon />
                                    </IconButton>
                                  </span>
                                </Tooltip>

                                <Tooltip title="Delete campaign">
                                  <IconButton onClick={() => confirmDelete(c)}>
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>

                                {canPause && (
                                  <Tooltip title="Pause campaign">
                                    <IconButton onClick={() => pauseCampaign(c.id)}>
                                      <PauseIcon />
                                    </IconButton>
                                  </Tooltip>
                                )}

                                {canResume && (
                                  <Tooltip title="Resume campaign">
                                    <IconButton onClick={() => resumeCampaign(c.id)}>
                                      <PlayIcon />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}

          {/* Tab: Calendar */}
          {tab === 1 && (
            <Box sx={{ p: { xs: 2, md: 2.5 } }}>
              <WhatsAppCalendar
                key={calendarKey}
                showHeader={true}
                onPlanNewBroadcast={openCreate}
                onSelectCampaign={handleCalendarSelect}
              />
            </Box>
          )}
        </Paper>

        <Box sx={{ mt: 2 }}>
          <WhatsAppContactInsightsCard
            reloadToken={calendarKey}
            timezone="Africa/Harare"
          />
        </Box>

        {/* Create / Edit */}
        {dialogMode === "create" && (
          <CreateCampaignModal
            open={dialogOpen}
            onClose={() => { setDialogOpen(false); setPendingIntelligenceId(null); }}
            onSuccess={async () => {
              setDialogOpen(false);
              setPendingIntelligenceId(null);
              await refreshAll();
            }}
            intelligenceAudienceId={pendingIntelligenceId}
          />
        )}

        {dialogMode === "edit" && (
          <CampaignDialog
            open={dialogOpen}
            mode="edit"
            initialCampaign={selectedCampaign}
            onClose={() => setDialogOpen(false)}
            onSaved={refreshAll}
          />
        )}

        <WhatsAppCampaignInsightsDialog
          open={insightsOpen}
          campaign={insightsCampaign}
          onClose={() => {
            setInsightsOpen(false);
            setInsightsCampaign(null);
          }}
        />

        <Dialog open={budgetDialogOpen} onClose={closeBudgetSettings} maxWidth="sm" fullWidth>
          <DialogTitle>WhatsApp Broadcast Budget</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Alert severity="info">
                This budget is shared across all WhatsApp campaigns and all users. When Meta balance is available,
                the remaining guard uses the lower of your manual cap and Meta remaining.
              </Alert>

              {budgetChannel?.meta?.billing?.available === false ? (
                <Alert severity="warning">
                  Live Meta billing balance is not exposed for this account yet, so the guard is currently using your
                  manual budget only.
                </Alert>
              ) : null}

              {budgetError ? <Alert severity="error">{budgetError}</Alert> : null}

              <TextField
                label="Daily limit (USD)"
                type="number"
                value={budgetForm.daily_limit}
                onChange={setBudgetField("daily_limit")}
                fullWidth
                inputProps={{ min: 0, step: "0.01" }}
                helperText="Leave blank if you do not want a daily cap."
              />

              <TextField
                label="Monthly limit (USD)"
                type="number"
                value={budgetForm.monthly_limit}
                onChange={setBudgetField("monthly_limit")}
                fullWidth
                inputProps={{ min: 0, step: "0.01" }}
                helperText="Leave blank if you do not want a monthly cap."
              />

              <TextField
                label="Reserve buffer (USD)"
                type="number"
                value={budgetForm.reserve_buffer}
                onChange={setBudgetField("reserve_buffer")}
                fullWidth
                inputProps={{ min: 0, step: "0.01" }}
                helperText="Held back from the visible remaining budget to avoid overspend."
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeBudgetSettings} disabled={budgetSaving}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={saveBudgetSettings}
              disabled={budgetSaving}
              sx={{ bgcolor: WA_GREEN, "&:hover": { bgcolor: "#219150" }, boxShadow: "none" }}
            >
              {budgetSaving ? "Saving..." : "Save budget"}
            </Button>
          </DialogActions>
        </Dialog>

        <ConfirmDeleteDialog
          open={deleteOpen}
          busy={deleteBusy}
          campaignName={selectedCampaign?.name}
          onClose={() => setDeleteOpen(false)}
          onConfirm={doDelete}
        />
      </Box>
    </Box>
  );
}
